import { dateFormat, dateTimeFormat, timeFormat } from "ApiConstraints";
import {
  format as dateFnsFormat,
  formatDistance as dateFnsFormatDistance,
  getYear,
  isValid as dateFnsIsValid,
  parse as dateFnsParse,
  parseISO,
} from "date-fns";
import { de, enUS, fr, it } from "date-fns/locale";
import { createLogger } from "logging";

const logger = createLogger("helpers/dates");

export type DateFieldValue = string | Date | undefined | null;
export const dateFormatWithWeekday = "EE, dd.MM.yyyy";
export const urlDateFormat = "yyyy-MM-dd";
export const minYear = 1950;

export const tryFormatDate = (value: DateFieldValue, formatStr: string) => {
  const date = parseDateLenient(value);
  if (!date) {
    logger.trace("tryFormatDate(): input is empty -> undefined.");
    return undefined;
  }

  if (!isValidDate(date)) {
    logger.trace("tryFormatDate(): input is not valid -> undefined.");
    return undefined;
  }

  if (!formatStr) {
    logger.trace("tryFormatDate(): format is empty -> undefined.");
    return undefined;
  }

  try {
    return dateFnsFormat(date, formatStr);
  } catch (ex) {
    logger.warn(`tryFormatDate(): ${date} with ${formatStr} returned error`, ex);
    return undefined;
  }
};

export const formatDate = (date: DateFieldValue, defaultValue = "") => {
  return tryFormatDate(date, dateFormat) || defaultValue;
};

export const formatDateTime = (date: DateFieldValue, defaultValue = "") => {
  return tryFormatDate(date, dateTimeFormat) || defaultValue;
};

export const formatTime = (date: DateFieldValue, defaultValue = "") => {
  return tryFormatDate(date, timeFormat) || defaultValue;
};

export const parseDate = (dateString: string | null, format: string = dateFormat): Date | null => {
  if (!dateString) return null;
  return dateFnsParse(dateString, format, new Date());
};

/**
 * Attempts to parse a date string using multiple formats, returning a `Date` object if successful or `null` if parsing fails.
 * This function is lenient and tries various common date formats.
 *
 * Supported formats:
 * - `d.M.yyyy` (e.g., '1.9.2024')
 * - `d.M.yy` (e.g., '1.9.24')
 * - `ddMMyyyy` (e.g., '01092024')
 * - 'dmyy' (e.g, '1124)
 *
 * If the date string is 4 digits (e.g., '1124'), it interprets it as `dmyy`.
 *
 * @example
 * parseDateLenient('1.9.2024');
 * returns Date object for Septembre 1, 2024
 *
 * @example
 * parseDateLenient('01092024');
 * returns Date object for Septembre 1, 2024
 *
 * @example
 * parseDateLenient('1124');
 * returns Date object for January 1, 2024
 */
export const parseDateLenient = (value: DateFieldValue): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  let result = undefined;

  for (const f of ["iso", dateFormat, dateFormatWithWeekday, "d.M.yyyy", "d.M.yy", "ddMMyyyy", "dd.MM.yyyy HH:mm"]) {
    const locale = getDateFnsLocale();
    result = f === "iso" ? parseISO(value) : dateFnsParse(value, f, new Date(), { locale });
    if (!isValidDate(result)) {
      continue;
    }

    logger.trace(
      "parseDateLenient(): ",
      value,
      `success with format '${f}' (${locale.code})  ->`,
      dateFnsFormat(result, "yyyy-mm-dd hh:MM:ss")
    );
    return result;
  }

  if (!isValidDate(result) && value.length === 4) {
    const d = parseInt(value.substring(0, 1));
    const m = parseInt(value.substring(1, 2));
    const y = parseInt(`20${value.substring(2)}`); // if 20 is not added, date is constructed as 1922.
    result = new Date(y, m - 1, d);

    if (isValidDate(result)) {
      logger.trace(
        "parseDateLenient(): ",
        value,
        "sucess with short form",
        y,
        m,
        d,
        dateFnsFormat(result, "yyyy-mm-dd HH:MM:ss")
      );
      return result;
    }
  }

  logger.warn("parseDateLenient(): ", value, "could not be parsed -> undefined");
  return undefined;
};

export const isDateNewerThanYesterday = (value: DateFieldValue): boolean => {
  const inputDate = parseDateLenient(value);
  if (!inputDate) {
    return true;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate yesterday's date
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const isNewer = inputDate > yesterday;
  logger.trace(`isDateNewerThanYesterday(): ${inputDate} -> ${isNewer}`);

  return isNewer;
};

export const formatDateRelative = (value: DateFieldValue, baseDate = new Date(), locale?: string) => {
  const date = parseDateLenient(value);
  if (!isValidDate(date)) {
    logger.warn(`formatDateRelative(): ${value} is invalid!`);
    return "";
  }

  const localeToUse = getDateFnsLocaleOrDefault(locale);
  const result = dateFnsFormatDistance(date!, baseDate, { addSuffix: true, locale: localeToUse });
  logger.trace(`formatDateRelative(): ${value} -> ${result}`);

  return result;
};

export function isValidDate(date: Date | undefined | null): boolean {
  if (!date) return false;
  if (!dateFnsIsValid(date)) return false;
  if (getYear(date) < minYear) return false;
  return true;
}

export function getDateFnsLocaleOrDefault(locale?: string) {
  switch (locale) {
    case "de":
    case "fr":
    case "it":
    case "en":
      return getDateFnsLocale(locale);
  }
  return getDateFnsLocale();
}

export function getDateFnsLocale(lang = `${document.documentElement.lang}`.toLowerCase()) {
  switch (lang) {
    case "fr":
      logger.trace(`getDateFnsLocale(): fr:=`, fr);
      return fr;
    case "en":
      logger.trace(`getDateFnsLocale(): en:=`, enUS);
      return enUS;
    case "it":
      logger.trace(`getDateFnsLocale(): it:=`, it);
      return it;
    case "de":
    default:
      logger.trace(`getDateFnsLocale(): default:= `, de);
      return de;
  }
}

export function formatEventTime(h: number, m: number) {
  const hlz = String(h).padStart(2, "0");
  const mlz = String(m).padStart(2, "0");
  return `${hlz}:${mlz}`;
}

export function isDateAfter(a: DateFieldValue, b: DateFieldValue) {
  const ad = parseDateLenient(a);
  const bd = parseDateLenient(b);
  return !!ad > !!bd;
}

export function isDateBefore(a: DateFieldValue, b: DateFieldValue) {
  const ad = parseDateLenient(a);
  const bd = parseDateLenient(b);
  return !!ad < !!bd;
}

export const toServerDate = (value: DateFieldValue): string | undefined => {
  let result = undefined;
  if (!value) {
    return result;
  }

  let date: Date | undefined;
  if (typeof value === "string") {
    date = parseDateLenient(value);
  } else if (value instanceof Date) {
    date = value;
  }

  if (date) {
    result = date.toISOString();
  }

  logger.trace(`toServerDate(): ${value} (${typeof value}) -> ${result}`);
  return result;
};
